import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import { useEffect, useId, useRef } from 'react';
import { get, useFormContext } from 'react-hook-form';

import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';
import { phone } from './form-validation-regex';
import { AutoComplete } from './form.constants';
import { FormError } from './form.error';
import { FormHelpMessage } from './form.help-message';
import { FormLabel } from './form.label';

import type { FieldError } from 'react-hook-form';

type Options = {
  code: string;
  callingCode?: string | null;
  name: string;
};

type Props = {
  containerClassName?: string;
  classname?: string;
  defaultValue?: string;
  label?: string;
  labelClassName?: string;
  loading?: boolean;
  options: Options[];
  required?: boolean;
  name?: string;
  addDefaultOption?: boolean;
  isDisabled?: boolean;
  phoneCountryError?: string;
};

export const normalizePhoneValue = (value: string) => value.replace(/\D/g, '');

export const FormInputPhone: React.FC<React.PropsWithChildren<Props>> = ({
  required,
  options,
  containerClassName,
  classname,
  labelClassName,
  defaultValue,
  label,
  loading,
  name = 'phone',
  addDefaultOption,
  isDisabled,
  phoneCountryError,
}) => {
  const id = useId();
  const helperId = `input-helper-${id}`;
  const helperPhoneId = `input-helper-phone-${id}`;
  const errorId = `input-error-${id}`;
  const phoneCountrySelectRef = useRef<HTMLSelectElement | null>(null);
  const [t] = useTranslation('osc-form');
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const phoneNumberFieldError: FieldError = get(errors, `${name}.phoneNumber`);
  const phoneCountryFieldError: FieldError = get(errors, `${name}.phoneCountry`);
  const hasError = !!phoneNumberFieldError || !!phoneCountryFieldError;
  const { ref: phoneCountryRef, ...phoneCountryRegister } = register(`${name}.phoneCountry`, {
    required: {
      value: true,
      message: phoneCountryError ? phoneCountryError : t('phone.phoneCountryError'),
    },
  });

  useEffect(() => {
    if (!phoneCountrySelectRef.current) {
      return;
    }
    const observer = new MutationObserver(() => {
      if (phoneCountrySelectRef.current) {
        setValue(`${name}.phoneCountry`, phoneCountrySelectRef.current.value, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
    observer.observe(phoneCountrySelectRef.current, {
      attributes: true,
      attributeFilter: ['selected'],
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
    };
  }, [name, setValue]);

  return (
    <div
      className={cx({
        [containerClassName as string]: !!containerClassName,
      })}
    >
      <div>
        <FormLabel
          label={label || t('phone.label')}
          required={required}
          hasError={hasError}
          className={cx({
            [labelClassName as string]: !!labelClassName,
          })}
        >
          <span className="flex flex-row">
            {loading ? (
              <SkeletonLoader className="h-12 w-1/3" variant="rounded" />
            ) : (
              <select
                defaultValue={defaultValue}
                autoComplete={AutoComplete.COUNTRY}
                className={cx('form-select w-1/3 overflow-hidden focus:relative focus:z-0', {
                  'form-error': !!phoneCountryFieldError,
                  disabled: isDisabled,
                  [classname as string]: !!classname,
                })}
                disabled={isDisabled}
                data-testid="phone.phoneCountry.secondary"
                aria-invalid={!!phoneCountryFieldError}
                aria-describedby={helperId}
                {...phoneCountryRegister}
                ref={(element) => {
                  phoneCountryRef(element);
                  phoneCountrySelectRef.current = element;
                }}
              >
                {addDefaultOption ? (
                  <option key="selectCountry" value="" disabled>
                    {t('phone.phoneCountrySelectOption')}
                  </option>
                ) : null}
                {options?.map(({ name, code, callingCode }) => (
                  <option
                    key={`option-${code}`}
                    value={`${callingCode}${code}`}
                    {...(`${callingCode}${code}` === defaultValue ? { selected: true } : {})}
                  >
                    {t('phone.phoneCallingCode', { name, callingCode })}
                  </option>
                ))}
              </select>
            )}

            <input
              autoComplete={AutoComplete.TEL}
              type="tel"
              className={cx('form-input -ml-0.5 flex-1 focus:relative focus:z-0', {
                'form-error': !!phoneNumberFieldError,
                [classname as string]: !!classname,
              })}
              aria-invalid={!!phoneNumberFieldError}
              aria-describedby={`${errorId} ${helperPhoneId}`}
              data-testid="phone.phoneNumber"
              maxLength={15}
              {...register(`${name}.phoneNumber`, {
                required: { value: true, message: t('phone.phoneNumberError') },
                pattern: { value: phone, message: t('phone.phoneNumberError') },
                setValueAs: normalizePhoneValue,
              })}
              disabled={isDisabled}
            />
          </span>
        </FormLabel>
        {phoneCountryFieldError && <FormError id={errorId} error={phoneCountryFieldError} />}
        {phoneNumberFieldError && <FormError id={errorId} error={phoneNumberFieldError} />}
        <FormHelpMessage id={helperId} isLoading={loading} />
        <FormHelpMessage id={helperPhoneId} message={t('phone.label')} className="!hidden" />
      </div>
    </div>
  );
};
